import {FC, SyntheticEvent, useMemo} from 'react';

import {Box, Skeleton, useMediaQuery} from '@mui/material';
import classNames from 'classnames';
import {t} from 'i18next';

import {AddToCartNormalIcon} from 'assets/icons/addToCartNormal';
import {Coin} from 'assets/icons/icon';
import {PurpleTag} from 'assets/icons/recommendation-icons/PurpleTag';
import {FloatingQuantityPickerWrapper} from 'components/floating-quantity-picker/FloatingQuantityPickerWrapper';
import {config} from 'config/env';
import i18n from 'config/i18n/i18n';
import {fallbackImage, isBasketDiversifyFlagEnabled} from 'constants/recommendation';
import {BASKET_PDP_ATTRIBUTES} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {useCounter} from 'hooks/recommendation-hooks/useCounter';
import {useFloatingQuantityPicker} from 'hooks/recommendation-hooks/useFloatingQuantityPicker';
import {usePromotionRibbon} from 'hooks/recommendation-hooks/usePromotionRibbon';
import {useAppMode} from 'hooks/useAppMode';
import {useCurrentBasketItem} from 'hooks/useCurrentBasketItem';
import {useProductAvailability} from 'hooks/useProductAvailability';
import {useProductPriceAndText} from 'hooks/useProductPriceAndText';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {JSONObject, Product} from 'types';
import {CollectionFrameModel, CollectionType} from 'types/recommedation-types/recommendations';
import {
  appsFlyerMarketingAnalytics,
  getCurrencySymbol,
  marketingAnalytics,
  productAnalytics,
  ProductAvailability
} from 'utils';
import {
  getBasketRecommendationAnalyticTitle,
  getPromotionTagStyle,
  shoppingProductGetPromotionRibbons,
  shoppingProductGetPromotionRibbonsReturnType
} from 'utils/recommendation-utils/recommendations';

import MarketingBadge from './marketing-badge';
import {
  AddToCartBtnHolder,
  DisplayText,
  DisplayTitleWrapper,
  ProductCardLEOContainer,
  ProductImageContainer,
  StyledAddToCartWrapper,
  StyledBrandPromoImage,
  StyledBrandPromoWrapper,
  StyledFloatingQuantityPickerContainer,
  StyledFooterContainer,
  StyledImageCardContainer,
  StyledOutofContainer,
  StyledOutofText,
  StyledProductContainer,
  StyledProductDeliveryWrapper,
  StyledProductWrapper,
  StyledPromoContainer
} from './styles';
import {ProductCardProps} from './types';
import {ImageCard} from '../imageCard';
import {UseImageCardProps} from '../imageCard/types';
import {Collection} from '../leo-promotions-badge/types';
import ProductDeliveryInfoViewV2 from '../plp_delivery_Info/product-delivery-info-view-v2';
import {deliveryInfoParser} from '../plp_delivery_Info/utils';
import ProductAddToCart from '../product-add-to-cart/product-add-to-cart';
import {
  StyledDiscountAddToBasketBtn,
  StyledUpdatedQuanityTypography
} from '../product-card-leodiscount-or-slab-price-box/styles';
import ProductLoyalityPromoView from '../product-loyalty-promo-view/product-loyality-promo.view';

export const ProductCardV2: FC<ProductCardProps> = ({
  sendCarouselImpressionAnalytics,
  product,
  carouselIndex,
  itemIndex
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const {currentStore, defaultMembershipCard} = useAppSelector((state) => state?.app);
  const {idToken: accessToken} = useAppSelector((state) => state?.auth);
  const {recommendations, complexPromotion} = useAppSelector((state) => state.recommendations);
  const {isExpress} = useAppMode();
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const {basketItem} = useCurrentBasketItem(product);
  const isProductServiceDataReady = product?.variants?.length > 0;
  const quantity = basketItem?.quantity || 0;

  const {
    isDisabled,
    status: productStatus,
    isOutOfStock,
    isNotEligible,
    isNotDeliverable: isOutsideZone
  } = useProductAvailability(product);

  const isProductNotEligible = productStatus === ProductAvailability.notEligible;
  const isMarketingBadgeV2Enabled = config.RECOM_SHOPPING_MARKETING_BADGE_V2_ENABLED;

  const addAnalytics = (productId?: string, analyticsProps?: any) => {
    sendCarouselImpressionAnalytics && sendCarouselImpressionAnalytics();
    productId && SendBrowserEvent.goBasketRecommendationToPDP(product.id, analyticsProps);
  };

  const ribbons = shoppingProductGetPromotionRibbons(product as any);
  const promoImages = isMarketingBadgeV2Enabled
    ? ribbons?.marketingBadge
    : ribbons?.rightRibbonInfo?.map((item) => item?.imageUrl);

  const isThai = i18n.language === 'th';
  const frameCollections: any = product?.collections
    ? product.collections.find((collection: Collection) => {
        const hasFrame = collection.frame != null && collection.frame !== undefined;

        return hasFrame;
      })
    : null;
  const collectionFrame =
    ReactNative.isReactNativeWebView() || isMobile
      ? frameCollections?.frame?.find((frameItem: CollectionFrameModel) => frameItem.platform === 'APP')
      : frameCollections?.frame?.find((frameItem: CollectionFrameModel) => frameItem.platform === 'WEB');
  const frameStyles = Object.fromEntries(
    Object.entries({
      top: collectionFrame?.top,
      bottom: collectionFrame?.bottom,
      left: collectionFrame?.left,
      right: collectionFrame?.right
    }).filter(([_, value]) => value != null && value !== 0)
  );
  const frameImageUrl = collectionFrame?.[isThai ? 'frameImage' : 'frameImageEn'];
  const showFrame = !!frameImageUrl;

  const deliveryInfo = isExpress
    ? deliveryInfoParser(product?.deliveryDescription?.singleLineV2 ?? '')
    : deliveryInfoParser(product?.deliveryDescription?.singleLine ?? '');
  const isFloatingPickerEnabled = config.RECOM_SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER;
  const isPlpProPointPromotionEnabled = config.RECOM_SHOPPING_PRODUCTLIST_PROPOINT_PROMOTION_ENABLE;
  const {discountPercentage, slabPriceInfoText, kgText, productOriginalPrice, productDisplayPrice, isDiscounted} =
    useProductPriceAndText({
      product,
      productCount: quantity,
      defaultSlabPriceTire: false,
      isMobile
    });

  const analyticProps = {
    algorithm: recommendations.algorithm,
    quantity,
    indexClicked: itemIndex,
    position: product?.position,
    getPersonalizedRecommendationsFromDb: recommendations.recommendationType,
    isDiversifyResult: isBasketDiversifyFlagEnabled,
    zipcode: currentStore?.zipcode ?? '',
    isLoggedIn: accessToken ? true : false,
    hasMMID: defaultMembershipCard?.id ? true : false,
    ...(product?.recommendationScore !== undefined && {
      recommendationScore: product?.recommendationScore
    }),
    ...(product?.normalisedRecommendationScore !== undefined && {
      normalisedRecommendationScore: product?.normalisedRecommendationScore
    }),
    ...(product?.alreadyPurchased !== undefined && {
      hasAlreadyPurchased: product?.alreadyPurchased
    }),
    ...(product?.isSponsored !== undefined && {
      isSponsored: product?.isSponsored
    })
  };

  const iconSize = ReactNative.isReactNativeWebView() ? 28 : 32;
  const {togglePicker, showPicker, closePicker} = useFloatingQuantityPicker({
    id: `${product.id}_${product.makroId}`
  });
  const showQuantityPicker = showPicker && isFloatingPickerEnabled;
  const isLoyaltyPromoV2Enabled = config.RECOM_LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED;

  const showKg = product?.isWeightedFresh;
  const enablePartialQuantity = showKg && product?.moq === 1 && product?.sellInPiece === false;
  const {count, inProgress, onAdd, onIncrement, onChange, onDecrement, onDelete} = useCounter({
    initialCount: basketItem?.quantity,
    enablePartialQuantity,
    product,
    closePicker,
    sendCarouselImpressionAnalytics,
    carouselAnalyticsProps: analyticProps
  });
  const loyaltyPromotionsData = usePromotionRibbon(product as any);

  const addToCartTitle = isOutOfStock
    ? t('labels.outOfStock')
    : isNotEligible
      ? t('labels.notEligible')
      : isOutsideZone
        ? t('labels.notDeliverable')
        : productDisplayPrice.stringValue?.split(getCurrencySymbol())[0]?.trim();

  const getFinalPromoData = (promotions: any, ribbons: shoppingProductGetPromotionRibbonsReturnType) => {
    if (isPlpProPointPromotionEnabled && promotions?.title) {
      return {
        label: promotions?.title,
        icon: promotions.type === CollectionType.PROPOINT_PROMOTION ? <Coin width={16} height={16} /> : <PurpleTag />,
        count: promotions.totalPromotions,
        type: promotions.type,
        loading: promotions.complexPromotionLoading,
        titleTextColor: promotions.titleTextColor,
        bgColor: promotions.bgColor,
        iconUrl: promotions?.iconUrl
      };
    }
    return {
      hasImageUrl: ribbons?.promotionInfoWithUrl?.imageUrl ?? undefined
    };
  };

  const productMedia: UseImageCardProps = {
    borderRadius: 'default',
    component: 'img',
    aspectRatio: '1/1',
    loading: 'lazy',
    src: product.imageUrls[0] || fallbackImage
  };

  const getPromoData = useMemo(() => {
    if (complexPromotion) {
      const {bgColor, txtColor} = getPromotionTagStyle(loyaltyPromotionsData.type);
      const loyalityPromoInfo = {
        ...loyaltyPromotionsData,
        titleTextColor: txtColor,
        bgColor: bgColor
      };

      const finalComplexPromotionData = getFinalPromoData(loyalityPromoInfo, ribbons);
      return finalComplexPromotionData;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexPromotion]);

  const renderFloatingQuantityPicker = () =>
    showQuantityPicker && (
      <StyledFloatingQuantityPickerContainer
        data-qty-picker
        className={classNames({
          isDiscounted: isDiscounted,
          slabPriceInfoText: slabPriceInfoText,
          isProductCardV2: true
        })}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <FloatingQuantityPickerWrapper
          max={product.totalInventory}
          disabled={inProgress}
          onChange={onChange}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          showKg={showKg}
          moq={product?.moq}
          isPDP={false}
          initialValue={count}
          onDelete={onDelete}
          size={'medium'}
          enablePartialQuantity={enablePartialQuantity}
        />
      </StyledFloatingQuantityPickerContainer>
    );

  const onPlusIconClick = async (e: SyntheticEvent<Element, Event>) => {
    e.stopPropagation();
    if (isFloatingPickerEnabled) {
      if (count === 0 && onAdd) {
        await onAdd(e);
        togglePicker();
      } else {
        togglePicker();
      }
    } else {
      handleProductClick(BASKET_PDP_ATTRIBUTES.TRIGGER_TYPE_PRODUCT_CARD);
    }
  };

  const StyledNoDiscountAddToBasketBtnAnchor = (
    <StyledDiscountAddToBasketBtn
      data-test-id={'btn_add_to_basket'}
      className={classNames({
        updatedQuantityBgColor: quantity,
        normalQuantityBgColor: quantity === 0
      })}
      onClick={onPlusIconClick}
    >
      {renderFloatingQuantityPicker()}
      {quantity ? (
        <StyledUpdatedQuanityTypography
          className={classNames({
            medium: quantity?.toString().length === 4,
            small: quantity?.toString().length === 5,
            extraSmall: quantity?.toString().length > 5
          })}
        >
          {quantity}
        </StyledUpdatedQuanityTypography>
      ) : (
        <AddToCartNormalIcon width={iconSize} height={iconSize} />
      )}
    </StyledDiscountAddToBasketBtn>
  );

  const handleProductClick = (triggerType: string) => {
    !isReactNativeView && SendBrowserEvent.closeBasketDrawer();
    const analyticsProps = {
      carouselIndex,
      carouselName: `${t(getBasketRecommendationAnalyticTitle(recommendations.recommendationType ?? ''))}`,
      algorithm: recommendations.algorithm,
      quantity,
      indexClicked: itemIndex,
      triggerType,
      position: product?.position,
      getPersonalizedRecommendationsFromDb: recommendations.recommendationType,
      isDiversifyResult: isBasketDiversifyFlagEnabled,
      ...(product?.recommendationScore !== undefined && {
        recommendationScore: product?.recommendationScore
      }),
      ...(product?.normalisedRecommendationScore !== undefined && {
        normalisedRecommendationScore: product?.normalisedRecommendationScore
      }),
      ...(product?.alreadyPurchased !== undefined && {
        hasAlreadyPurchased: product?.alreadyPurchased
      }),
      ...(productAnalytics(product) as JSONObject),
      ...(!isReactNativeView && marketingAnalytics(product as Product)),
      ...(isReactNativeView && appsFlyerMarketingAnalytics(product)),
      ...(product?.isSponsored !== undefined && {
        isSponsored: product?.isSponsored
      }),
      ...(isReactNativeView && {
        fromExpress: isExpress
      })
    };
    addAnalytics(product.id, analyticsProps);
  };

  const productImage = (
    <ProductImageContainer
      showFrame={showFrame}
      aspectRatio={productMedia.aspectRatio}
      scaleFactor={collectionFrame?.scale || 1}
    >
      <ImageCard
        {...productMedia}
        skeletonLoading={!isProductServiceDataReady}
        ref={null}
        showFrame={showFrame}
        frameStyles={frameStyles}
        isMarketingBadgeV2={
          !showFrame &&
          isMarketingBadgeV2Enabled &&
          !Array.isArray(promoImages) &&
          promoImages &&
          promoImages.title &&
          promoImages?.type
        }
      >
        {isDisabled && (
          <StyledOutofContainer>
            <StyledOutofText boxShadow={1}>{productStatus}</StyledOutofText>
          </StyledOutofContainer>
        )}
      </ImageCard>
    </ProductImageContainer>
  );

  const frameProductImage = (
    <ImageCard
      src={frameImageUrl}
      borderRadius="default"
      skeletonLoading={false}
      component="img"
      style={{zIndex: 1}}
      aspectRatio="1/1"
      showFrame={showFrame}
      frameStyles={frameStyles}
    >
      {productImage}
    </ImageCard>
  );

  const handleProductCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-qty-picker]')) {
      return;
    }
    handleProductClick(BASKET_PDP_ATTRIBUTES.TRIGGER_TYPE_PRODUCT_CARD);
  };

  return (
    <ProductCardLEOContainer onClick={() => addAnalytics()}>
      <Box onClick={(e) => handleProductCardClick(e)}>
        <StyledProductContainer data-productid={product?.id}>
          <StyledProductWrapper>
            <StyledImageCardContainer>
              {showFrame ? frameProductImage : productImage}
              {!showFrame ? (
                isMarketingBadgeV2Enabled &&
                !Array.isArray(promoImages) &&
                promoImages &&
                promoImages.title &&
                promoImages?.type ? (
                  <MarketingBadge
                    text={promoImages?.title}
                    type={promoImages?.type}
                    accessibilityId={promoImages?.accessibilityId ?? ''}
                  />
                ) : (
                  Array.isArray(promoImages) &&
                  promoImages.length > 0 && (
                    <StyledBrandPromoWrapper data-test-id={`${product?.id}_promotion_icons`}>
                      <StyledBrandPromoImage src={promoImages[0]} alt="promo" />
                    </StyledBrandPromoWrapper>
                  )
                )
              ) : (
                <></>
              )}
              {!isDisabled && (
                <AddToCartBtnHolder className={showQuantityPicker ? 'normal' : ''}>
                  {StyledNoDiscountAddToBasketBtnAnchor}
                </AddToCartBtnHolder>
              )}
            </StyledImageCardContainer>
            <StyledPromoContainer>
              {loyaltyPromotionsData?.complexPromotionLoading ? (
                <Skeleton variant="rounded" width={'50%'} height={'20px'} />
              ) : (
                <ProductLoyalityPromoView
                  isShowLoyaltyCampaignPromoV2={isLoyaltyPromoV2Enabled}
                  isProductCardV2={true}
                  {...getPromoData}
                />
              )}
            </StyledPromoContainer>
            <StyledFooterContainer>
              <DisplayTitleWrapper data-test-id={`${product.id}_title1`}>
                <DisplayText>{product?.title}</DisplayText>
              </DisplayTitleWrapper>
            </StyledFooterContainer>
            <StyledProductDeliveryWrapper>
              <ProductDeliveryInfoViewV2 {...deliveryInfo} />
            </StyledProductDeliveryWrapper>
            <StyledAddToCartWrapper>
              <ProductAddToCart
                currency={product?.priceUnit}
                mode={discountPercentage || slabPriceInfoText ? 'primary' : isDisabled ? 'text' : 'compact'}
                title={addToCartTitle}
                loading={!isProductServiceDataReady}
                isAddToCartLoading={inProgress}
                isOutOfStock={isOutOfStock}
                restrictedText={isProductNotEligible}
                isProductCardV2={true}
                discountPercentage={discountPercentage}
                strikeText={productOriginalPrice?.stringValue}
                slabPriceText={slabPriceInfoText}
                kgText={kgText}
              />
            </StyledAddToCartWrapper>
          </StyledProductWrapper>
        </StyledProductContainer>
      </Box>
    </ProductCardLEOContainer>
  );
};
